import { useState, useEffect, useMemo } from 'react';
import {
  Brain,
  Search,
  Filter,
  Calendar,
  User,
  Clock,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Sparkles,
  RefreshCw,
  AlertCircle,
  Phone
} from 'lucide-react';
import { useLanguage } from '../../hooks';
import { auditService } from '../../services/api';
import { getActivityMeta, formatActivityMessage, ACTIVITY_TYPES, formatCurrency, formatDate } from '../../utils/helpers';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui';

// Module-level cache to prevent refetching across tab switches
let cachedState = {
  logs: null,
  page: 1,
  hasMore: true,
  searchTerm: '',
  filterType: 'all',
  filterUser: 'all',
  dateRange: '7',
  loadedDateRange: null
};

// eslint-disable-next-line no-unused-vars
export default function AuditLogsView({ leads, currentUser, users: allUsers = [], auditLogs = [], setAuditLogs }) {
  const { t } = useLanguage();
  const [logs, setLogs] = useState(cachedState.logs || []);
  const [loading, setLoading] = useState(!cachedState.logs);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(cachedState.page);
  const [hasMore, setHasMore] = useState(cachedState.hasMore);
  const [searchTerm, setSearchTerm] = useState(cachedState.searchTerm);
  const [filterType, setFilterType] = useState(cachedState.filterType);
  const [filterUser, setFilterUser] = useState(cachedState.filterUser);
  const [dateRange, setDateRange] = useState(cachedState.dateRange);
  const [expandedLog, setExpandedLog] = useState(null);

  // Sync real-time logs from App.jsx
  useEffect(() => {
    if (auditLogs.length > 0) {
      setLogs(prev => {
        const newLogs = [...auditLogs, ...prev];
        // Ensure unique logs
        return newLogs.filter((v, i, a) => a.findIndex(v2 => v2.id === v.id) === i);
      });
      // Clear the incoming queue so we don't keep adding the same ones
      if (setAuditLogs) setAuditLogs([]);
    }
  }, [auditLogs, setAuditLogs]);


  // Sync state to cache
  useEffect(() => {
    cachedState = {
      ...cachedState,
      logs,
      page,
      hasMore,
      searchTerm,
      filterType,
      filterUser,
      dateRange
    };
  }, [logs, page, hasMore, searchTerm, filterType, filterUser, dateRange]);

  // Fetch audit logs from backend
  const fetchLogs = async (pageNum = 1, append = false) => {
    if (!append) setLoading(true);
    else setLoadingMore(true);

    try {
      const days = parseInt(dateRange, 10) || 7;
      const now = new Date();
      const from = new Date();
      from.setDate(now.getDate() - days);
      
      const queryParams = {
        limit: 50,
        page: pageNum,
        dateFrom: from.toISOString()
      };

      const json = await auditService.getAll(queryParams);
      
      if (!json.status) {
        console.error('Error fetching audit logs:', json.message);
        if (!append) setLogs([]);
        return;
      }

      const fetchedLogs = json.data || [];
      if (append) {
        setLogs(prev => {
          const newLogs = [...prev, ...fetchedLogs];
          // Ensure unique logs just in case
          return newLogs.filter((v,i,a) => a.findIndex(v2 => v2.id === v.id) === i); 
        });
      } else {
        setLogs(fetchedLogs);
      }
      
      setHasMore(fetchedLogs.length === 50);
      setPage(pageNum);
      cachedState.loadedDateRange = dateRange;
      
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    // If it's already loaded and the date filter hasn't changed, use cache
    if (cachedState.logs && cachedState.loadedDateRange === dateRange) {
      return;
    }
    fetchLogs(1, false);
  }, [dateRange]);

  const handleRefresh = () => {
    fetchLogs(1, false);
  };

  // Get users for dropdown (prefer full user list from props; fallback to users found in logs)
  const users = useMemo(() => {
    if (allUsers && allUsers.length > 0) {
      const names = allUsers
        .map(u => u.username || u.email)
        .filter(Boolean);
      return Array.from(new Set(names)).sort();
    }

    const unique = [...new Set(logs.map(l => l.user?.username || l.user?.email).filter(Boolean))];
    return unique.sort();
  }, [logs, allUsers]);

  // Get interaction types
  const interactionTypes = [
    { value: 'all', label: t('allTypes') || 'All types' },
    { value: 'AI', label: t('aiActions') || 'AI' },
    { value: 'Lead', label: t('leadActions') || 'Leads' },
    { value: 'Call', label: t('callActions') || 'Calls' },
    { value: 'Payment', label: t('paymentActions') || 'Payments' }
  ];

  // Filter logs
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      if (filterType !== 'all' && log.entity !== filterType) return false;
      if (filterUser !== 'all' && (log.user?.username || log.user?.email) !== filterUser) return false;
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          log.action?.toLowerCase().includes(search) ||
          log.entity?.toLowerCase().includes(search) ||
          log.entityId?.toLowerCase().includes(search) ||
          JSON.stringify(log.metadata || {}).toLowerCase().includes(search)
        );
      }
      return true;
    });
  }, [logs, filterType, filterUser, searchTerm]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    let relativeTime = '';
    if (diffInSeconds < 60) relativeTime = 'Just now';
    else if (diffInMinutes < 60) relativeTime = `${diffInMinutes}m ago`;
    else if (diffInHours < 24) relativeTime = `${diffInHours}h ago`;
    else if (diffInDays < 7) relativeTime = `${diffInDays}d ago`;
    else relativeTime = formatDate(date, { month: 'short', day: 'numeric' });

    return {
      relative: relativeTime,
      full: date.toLocaleString('en-PK', {
        dateStyle: 'medium',
        timeStyle: 'short'
      })
    };
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'AI': return <Brain className="w-5 h-5" />;
      case 'Lead': return <User className="w-5 h-5" />;
      case 'Payment': return <Sparkles className="w-5 h-5 text-emerald-500" />;
      case 'Call': return <Phone className="w-5 h-5 text-amber-500" />;
      default: return <Filter className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'AI': return 'bg-purple-50 text-purple-600 border-purple-100';
      case 'Lead': return 'bg-blue-50 text-blue-600 border-blue-100';
      case 'Payment': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      case 'Call': return 'bg-amber-50 text-amber-600 border-amber-100';
      default: return 'bg-gray-50 text-gray-600 border-gray-100';
    }
  };

  const getActionColor = (action) => {
    const act = action?.toUpperCase() || '';
    if (act.includes('CREATE')) return 'text-emerald-600 bg-emerald-50 border-emerald-100';
    if (act.includes('DELETE')) return 'text-red-600 bg-red-50 border-red-100';
    if (act.includes('UPDATE')) return 'text-blue-600 bg-blue-50 border-blue-100';
    if (act.includes('IMPORT')) return 'text-purple-600 bg-purple-50 border-purple-100';
    return 'text-gray-600 bg-gray-50 border-gray-100';
  };

  const getTypeLabel = (type) => {
    const found = interactionTypes.find(t => t.value === type);
    return found?.label || type || 'Interaction';
  };

  // Helper to get username by ID
  const getUserNameById = (userId) => {
    if (!userId) return 'Unassigned';
    if (!allUsers || allUsers.length === 0) return userId;
    // Map userId to Number just in case
    const user = allUsers.find(u => Number(u.id) === Number(userId));
    return user ? (user.username || user.email) : userId;
  };

  // Helper to get contact phone by ID
  const getContactPhoneById = (contactId) => {
    if (!contactId) return 'N/A';
    if (!leads || leads.length === 0) return contactId;
    
    // Find matching lead that contains this contact
    const leadWithContact = leads.find(l => l.contact && Number(l.contact.id) === Number(contactId));
    
    if (leadWithContact && leadWithContact.contact?.phone) {
      return leadWithContact.contact.phone;
    }
    return contactId;
  };

  // Helper to format values for display
  const formatValue = (val) => {
    if (val === null || val === undefined || val === '') return <span className="text-gray-400 italic">Empty</span>;
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'object') {
      // Handle nested objects like contact, source, assignee
      const details = [];
      if (val.firstName || val.lastName) details.push(`${val.firstName || ''} ${val.lastName || ''}`.trim());
      else if (val.name) details.push(val.name);
      else if (val.username) details.push(val.username);
      else if (val.title) details.push(val.title);
      
      if (val.email) details.push(val.email);
      if (val.phone) details.push(val.phone);
      
      if (details.length > 0) return details.join(' • ');
      
      return <pre className="text-xs bg-gray-50 border p-1 rounded mt-1">{JSON.stringify(val, null, 2)}</pre>;
    }
    // Check if it's a date string
    if (typeof val === 'string') {
      if (val.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        return new Date(val).toLocaleString('en-PK', { dateStyle: 'medium', timeStyle: 'short' });
      }
      // Try to parse JSON strings
      if ((val.startsWith('{') && val.endsWith('}')) || (val.startsWith('[') && val.endsWith(']'))) {
        try {
          const parsed = JSON.parse(val);
          return <pre className="text-[10px] bg-gray-50 border border-gray-100 p-2 rounded mt-1 font-mono text-gray-600 leading-relaxed overflow-x-auto">{JSON.stringify(parsed, null, 2)}</pre>;
        } catch (e) {
          // Not valid JSON or truncated JSON, continue
        }
      }
    }
    return String(val);
  };

  // Helper to render human-readable changes (Before -> After)
  const renderDiff = (before, after) => {
    if (!before || !after) return null;
    const changes = [];
    
    Object.keys(after).forEach(key => {
      if (['updatedAt', 'createdAt', 'id', 'contactId', 'sourceId', 'assignedTo'].includes(key)) return;
      
      const beforeVal = before[key];
      const afterVal = after[key];
      
      const isObject = typeof beforeVal === 'object' || typeof afterVal === 'object';
      const beforeStr = isObject ? JSON.stringify(beforeVal) : beforeVal;
      const afterStr = isObject ? JSON.stringify(afterVal) : afterVal;
      
      if (beforeStr !== afterStr && !(beforeVal == null && afterVal == null)) {
        changes.push({
          key,
          old: formatValue(beforeVal),
          new: formatValue(afterVal)
        });
      }
    });

    if (changes.length === 0) {
      return <p className="text-xs text-gray-400 italic py-2 px-4 bg-gray-50 rounded-lg">No visible changes recorded</p>;
    }

    return (
      <div className="overflow-hidden rounded-xl border border-gray-100 shadow-sm">
        <table className="w-full text-xs text-left">
          <thead className="bg-gray-50/80 text-gray-500 uppercase tracking-wider font-bold">
            <tr>
              <th className="px-4 py-2.5">Field</th>
              <th className="px-4 py-2.5">Previous</th>
              <th className="px-4 py-2.5 text-blue-600">New Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {changes.map((change, idx) => (
              <tr key={idx} className="bg-white hover:bg-gray-50/50 transition-colors">
                <td className="px-4 py-2.5 font-semibold text-gray-700 capitalize">
                  {change.key.replace(/([A-Z])/g, ' $1').trim()}
                </td>
                <td className="px-4 py-2.5 text-gray-400 line-through decoration-gray-300">
                  {change.old}
                </td>
                <td className="px-4 py-2.5 text-blue-700 font-medium bg-blue-50/20">
                  {change.new}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderMetadata = (log) => {
    const { action, metadata } = log;
    if (!metadata) return null;

    if (action === 'UPDATE_LEAD') {
      if (metadata.before && metadata.after) {
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider px-1">
              <Sparkles className="w-3.5 h-3.5 text-blue-500" />
              Modification History
            </div>
            {renderDiff(metadata.before, metadata.after)}
          </div>
        );
      }
    }

    if (action === 'CREATE_LEAD' && metadata.lead) {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider px-1">
            <User className="w-3.5 h-3.5 text-emerald-500" />
            New Lead Profile
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-white p-4 rounded-xl border border-emerald-100 shadow-sm">
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase font-bold text-gray-400 tracking-tight">Lead Title</span>
              <span className="text-sm font-semibold text-gray-800">{metadata.lead.title || 'Untitled Lead'}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase font-bold text-gray-400 tracking-tight">Status</span>
              <div>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-600 border border-blue-100 uppercase">
                  {metadata.lead.status}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase font-bold text-gray-400 tracking-tight">Phone Number</span>
              <span className="text-sm font-medium text-gray-700">{getContactPhoneById(metadata.contactId)}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase font-bold text-gray-400 tracking-tight">Assigned To</span>
              <span className="text-sm font-medium text-gray-700">{getUserNameById(metadata.lead.assignedTo)}</span>
            </div>
          </div>
        </div>
      );
    }

    if (action === 'CALL_LOGGED' || (action === 'UPDATE_LEAD' && metadata.outcome)) {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider px-1">
            <Phone className="w-3.5 h-3.5 text-amber-500" />
            Interaction Summary
          </div>
          <div className="bg-white p-4 rounded-xl border border-amber-100 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase font-bold text-gray-400 tracking-tight">Outcome</span>
                <span className="text-sm font-bold text-amber-700">{metadata.outcome}</span>
              </div>
              {metadata.statusChangedTo && (
                <div className="flex flex-col gap-0.5 text-right">
                  <span className="text-[10px] uppercase font-bold text-gray-400 tracking-tight">Stage Update</span>
                  <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-100 uppercase">
                    {metadata.statusChangedTo}
                  </span>
                </div>
              )}
            </div>
            {metadata.notes && (
              <div className="pt-3 border-t border-gray-50">
                <span className="text-[10px] uppercase font-bold text-gray-400 tracking-tight block mb-1">Notes from call</span>
                <p className="text-sm text-gray-600 italic bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                  "{metadata.notes}"
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }
    
    if (action === 'PAYMENT_CREATED' || action === 'PAYMENT_DELETED') {
      const isDelete = action === 'PAYMENT_DELETED';
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider px-1">
            <Sparkles className={`w-3.5 h-3.5 ${isDelete ? 'text-red-500' : 'text-emerald-500'}`} />
            Financial Record
          </div>
          <div className={`bg-white p-4 rounded-xl border ${isDelete ? 'border-red-100' : 'border-emerald-100'} shadow-sm flex items-center justify-between`}>
            <div>
              <p className="text-xl font-black text-gray-900 tracking-tight">
                <span className="text-xs font-medium text-gray-400 mr-1">PKR</span>
                {Number(metadata.amount || 0).toLocaleString()}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${isDelete ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
                  {metadata.type}
                </span>
                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">• {metadata.shift} Shift</span>
              </div>
            </div>
            {metadata.hasNotes && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-50 border border-gray-100">
                <MessageSquare className="w-3 h-3 text-gray-400" />
                <span className="text-[10px] text-gray-500 font-medium">Notes attached</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (action === 'IMPORT_LEADS_BULK') {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider px-1">
            <RefreshCw className="w-3.5 h-3.5 text-purple-500" />
            Batch Import Statistics
          </div>
          <div className="flex gap-4">
            <div className="flex-1 bg-white p-4 rounded-xl border border-emerald-100 shadow-sm text-center">
              <div className="text-2xl font-black text-emerald-600 tracking-tighter">{metadata.totalImported || 0}</div>
              <div className="text-[10px] text-emerald-700 uppercase font-bold tracking-widest">Success</div>
            </div>
            <div className="flex-1 bg-white p-4 rounded-xl border border-red-100 shadow-sm text-center">
              <div className="text-2xl font-black text-red-600 tracking-tighter">{metadata.failedCount || 0}</div>
              <div className="text-[10px] text-red-700 uppercase font-bold tracking-widest">Failed</div>
            </div>
          </div>
        </div>
      );
    }

    if (action === 'ADD_NOTE' && metadata.type && metadata.content) {
      const meta = getActivityMeta(metadata.type);
      const data = tryParseOrExtract(metadata.content);
      const displayContent = formatActivityMessage({ type: metadata.type, ...data });

      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider px-1">
            <span className="text-lg">{meta.icon}</span>
            {meta.label || 'Note Details'}
          </div>
          <div className={`bg-white p-4 rounded-xl border border-${meta.color}-100 shadow-sm`}>
            <p className="text-sm font-medium text-gray-800 leading-relaxed">
              {displayContent}
            </p>
            {/* If parsing failed and we only extracted some fields, show a hint */}
            {typeof metadata.content === 'string' && metadata.content.includes('...') && (
              <p className="text-[10px] text-gray-400 mt-2 italic">
                * Note: Some details from this older log entry may be truncated.
              </p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider px-1">
          <AlertCircle className="w-3.5 h-3.5 text-gray-400" />
          Extended Details
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          {typeof metadata === 'object' && !Array.isArray(metadata) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              {Object.entries(metadata).map(([k, v]) => (
                <div key={k} className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">{k.replace(/([A-Z])/g, ' $1').trim()}</span>
                  <span className="text-sm text-gray-700 font-medium break-words">{formatValue(v)}</span>
                </div>
              ))}
            </div>
          ) : (
            <pre className="text-xs text-gray-600 overflow-x-auto font-mono bg-gray-50 p-3 rounded-lg border border-gray-100">
              {JSON.stringify(metadata, null, 2)}
            </pre>
          )}
        </div>
      </div>
    );
  };

  // Helper to extract values from truncated JSON strings
  const tryParseOrExtract = (jsonStr) => {
    if (!jsonStr || typeof jsonStr !== 'string') return {};
    
    try {
      return JSON.parse(jsonStr);
    } catch (e) {
      // If parsing fails, it might be truncated. Try to extract common fields using regex.
      const result = {};
      
      // Extract from/to for stage changes
      const fromMatch = jsonStr.match(/"from"\s*:\s*"([^"]+)"/i);
      if (fromMatch) result.from = fromMatch[1];
      
      const toMatch = jsonStr.match(/"to"\s*:\s*"([^"]+)"/i);
      if (toMatch) result.to = toMatch[1];
      
      // Extract amount for payments
      const amountMatch = jsonStr.match(/"amount"\s*:\s*(\d+(\.\d+)?)/i);
      if (amountMatch) result.amount = Number(amountMatch[1]);
      
      // Extract paymentType
      const typeMatch = jsonStr.match(/"paymentType"\s*:\s*"([^"]+)"/i);
      if (typeMatch) result.paymentType = typeMatch[1];

      // Extract notes
      const noteMatch = jsonStr.match(/"note"\s*:\s*"([^"]+)"/i);
      if (noteMatch) result.note = noteMatch[1];

      return result;
    }
  };

  // Helper to get human-readable summary of the action
  const getActionSummary = (log) => {
    const { action, metadata } = log;
    
    if (action === 'ADD_NOTE' && metadata.type && metadata.content) {
      const data = tryParseOrExtract(metadata.content);
      const formatted = formatActivityMessage({ type: metadata.type, ...data });
      
      // If formatActivityMessage returned a generic "Activity logged" or similar, 
      // and we have a label from metadata type, use that instead
      if ((formatted === 'Activity logged' || formatted.startsWith('{')) && metadata.type) {
        return metadata.type.replace(/_/g, ' ').toUpperCase();
      }
      return formatted;
    }
    
    if (action === 'CALL_LOGGED' && metadata.outcome) {
      return `Logged call: ${metadata.outcome}`;
    }
    
    if (action === 'PAYMENT_CREATED' && metadata.amount) {
      return `Added payment: ${formatCurrency(metadata.amount)} (${metadata.type})`;
    }

    if (action === 'PAYMENT_DELETED' && metadata.amount) {
      return `Deleted payment: ${formatCurrency(metadata.amount)} (${metadata.type})`;
    }
    
    if (action === 'UPDATE_LEAD') {
      return `Updated lead details`;
    }
    
    if (action === 'CREATE_LEAD') {
      return `Created new lead profile`;
    }

    if (action === 'IMPORT_LEADS_BULK') {
      return `Imported ${metadata.totalImported || 0} leads bulk`;
    }

    return log.action?.replace(/_/g, ' ') || 'System Operation';
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900 flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-xl shadow-lg shadow-blue-200">
              <Brain className="w-6 h-6 text-white" />
            </div>
            {t('auditLogs') || t('aiAuditLogs') || 'Audit Logs'}
          </h1>
          <p className="text-gray-500 mt-1.5 text-sm font-medium">
            {t('viewSystemAuditLogs') || t('viewAiHistory') || 'Centralized history of critical system actions'}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading || loadingMore}
          className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-white border border-gray-200 shadow-sm text-gray-700 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 flex items-center justify-center gap-2.5 transition-all font-bold text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loading && !cachedState.logs ? 'animate-spin text-blue-600' : 'text-gray-400'}`} />
          Refresh Feed
        </button>
      </div>

      {/* Filters Bar */}
      <div className="bg-white/80 backdrop-blur-md rounded-2xl border border-gray-200 shadow-sm p-3">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('searchLogs')}
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-gray-50/50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all placeholder:text-gray-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="h-8 w-[1px] bg-gray-100 hidden md:block" />

          {/* Type Filter */}
          <div className="flex items-center gap-2.5 px-1">
            <Filter className="w-4 h-4 text-gray-400" />
            <Dropdown className="w-auto">
              <DropdownTrigger>
                <button className="flex items-center gap-1.5 text-sm font-bold text-gray-700 bg-transparent outline-none cursor-pointer hover:text-blue-600 transition-colors">
                  {interactionTypes.find(t => t.value === filterType)?.label || filterType}
                  <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </DropdownTrigger>
              <DropdownPopover className="w-[180px]">
                <DropdownMenu
                  selectedKeys={new Set([filterType])}
                  selectionMode="single"
                  onSelectionChange={(keys) => setFilterType(Array.from(keys)[0])}
                >
                  {interactionTypes.map(type => (
                    <DropdownItem key={type.value} id={type.value}>{type.label}</DropdownItem>
                  ))}
                </DropdownMenu>
              </DropdownPopover>
            </Dropdown>
          </div>

          {/* User Filter */}
          <div className="flex items-center gap-2.5 px-1">
            <User className="w-4 h-4 text-gray-400" />
            <Dropdown className="w-auto">
              <DropdownTrigger>
                <button className="flex items-center gap-1.5 text-sm font-bold text-gray-700 bg-transparent outline-none cursor-pointer hover:text-blue-600 transition-colors">
                  {filterUser === 'all' ? t('allUsers') || 'All Users' : filterUser}
                  <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </DropdownTrigger>
              <DropdownPopover className="w-[180px]">
                <DropdownMenu
                  selectedKeys={new Set([filterUser])}
                  selectionMode="single"
                  onSelectionChange={(keys) => setFilterUser(Array.from(keys)[0])}
                >
                  <DropdownItem id="all">{t('allUsers') || 'All Users'}</DropdownItem>
                  {users.map(user => (
                    <DropdownItem key={user} id={user}>{user}</DropdownItem>
                  ))}
                </DropdownMenu>
              </DropdownPopover>
            </Dropdown>
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2.5 px-1">
            <Calendar className="w-4 h-4 text-gray-400" />
            <Dropdown className="w-auto">
              <DropdownTrigger>
                <button className="flex items-center gap-1.5 text-sm font-bold text-gray-700 bg-transparent outline-none cursor-pointer hover:text-blue-600 transition-colors">
                  {dateRange === '1' ? t('last24Hours') || 'Last 24 Hours' :
                   dateRange === '7' ? t('last7Days') || 'Last 7 Days' :
                   dateRange === '30' ? t('last30Days') || 'Last 30 Days' :
                   dateRange === '90' ? t('last90Days') || 'Last 90 Days' : dateRange}
                  <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </DropdownTrigger>
              <DropdownPopover className="w-[180px]">
                <DropdownMenu
                  selectedKeys={new Set([dateRange])}
                  selectionMode="single"
                  onSelectionChange={(keys) => setDateRange(Array.from(keys)[0])}
                >
                  <DropdownItem id="1">{t('last24Hours') || 'Last 24 Hours'}</DropdownItem>
                  <DropdownItem id="7">{t('last7Days') || 'Last 7 Days'}</DropdownItem>
                  <DropdownItem id="30">{t('last30Days') || 'Last 30 Days'}</DropdownItem>
                  <DropdownItem id="90">{t('last90Days') || 'Last 90 Days'}</DropdownItem>
                </DropdownMenu>
              </DropdownPopover>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Logs List Container */}
      <div className="flex-1 bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
        {loading ? (
          <div className="flex-1 overflow-y-auto px-4 py-2 space-y-3 mt-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="rounded-2xl border border-gray-100 bg-white p-4 flex gap-4">
                <Skeleton variant="rectangular" width={48} height={48} className="rounded-xl flex-shrink-0" animation="wave" />
                <div className="flex-1 space-y-3 py-1">
                  <div className="flex items-center gap-2">
                    <Skeleton width={80} height={18} animation="wave" />
                    <Skeleton width={16} height={16} variant="circular" animation="wave" />
                    <Skeleton width={60} height={18} animation="wave" />
                  </div>
                  <Skeleton width="60%" height={20} animation="wave" />
                  <div className="flex items-center gap-4 mt-2">
                    <Skeleton width={100} height={16} animation="wave" />
                    <Skeleton width={80} height={16} animation="wave" />
                  </div>
                </div>
                <Skeleton width={32} height={32} variant="circular" className="flex-shrink-0" animation="wave" />
              </div>
            ))}
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8 text-center">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-10 h-10 opacity-20" />
            </div>
            <p className="text-xl font-black text-gray-900 mb-1">{t('noAuditLogs')}</p>
            <p className="text-sm font-medium text-gray-500 max-w-xs">{t('auditLogsPlaceholder')}</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-4 py-2 scrollbar-thin scrollbar-thumb-gray-200">
            <div className="space-y-3 mt-2">
              {filteredLogs.map((log) => {
                const ts = formatTimestamp(log.createdAt);
                const isExpanded = expandedLog === log.id;
                
                return (
                  <div
                    key={log.id}
                    className={`group rounded-2xl border transition-all duration-300 ${
                      isExpanded 
                        ? 'border-blue-200 bg-blue-50/10 shadow-md ring-1 ring-blue-50' 
                        : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'
                    }`}
                  >
                    {/* Log Header Button */}
                    <button
                      onClick={() => setExpandedLog(isExpanded ? null : log.id)}
                      className="w-full p-4 text-left focus:outline-none"
                    >
                      <div className="flex items-center gap-4">
                        {/* Icon Badge */}
                        <div className={`flex-shrink-0 w-12 h-12 rounded-xl border flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${getTypeColor(log.entity)} shadow-sm`}>
                          {getTypeIcon(log.entity)}
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider border ${getActionColor(log.action)}`}>
                              {getActionSummary(log)}
                            </span>
                            <span className="text-[10px] font-bold text-gray-300 uppercase tracking-widest">•</span>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                              {getTypeLabel(log.entity)}
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-bold text-gray-900 truncate">
                              {log.lead ? (
                                <span className="flex items-center gap-1.5">
                                  {log.lead.contact?.firstName} {log.lead.contact?.lastName}
                                  <span className="text-gray-400 font-normal">({log.lead.contact?.phone})</span>
                                </span>
                              ) : log.entityId ? (
                                <span className="font-mono text-xs text-gray-500">#{log.entityId.slice(-8)}</span>
                              ) : (
                                'System Operation'
                              )}
                            </h3>
                          </div>

                          <div className="flex items-center gap-4 mt-2">
                            <div className="flex items-center gap-1.5">
                              <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center">
                                <User className="w-3 h-3 text-gray-500" />
                              </div>
                              <span className="text-xs font-bold text-gray-600">
                                {log.user?.username || log.user?.email || 'System'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-blue-50/80 border border-blue-100 shadow-sm">
                                <Clock className="w-3 h-3 text-blue-500" />
                                <span className="text-[10px] font-bold text-blue-700">
                                  {ts.relative}
                                </span>
                              </div>
                              <span className="text-[11px] font-medium text-gray-400">
                                ({ts.full})
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Expand Indicator */}
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isExpanded ? 'bg-blue-100 text-blue-600' : 'bg-gray-50 text-gray-400 group-hover:bg-gray-100 group-hover:text-gray-600'}`}>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Expanded Content Area */}
                    {isExpanded && (
                      <div className="px-5 pb-5 animate-in slide-in-from-top-2 duration-300">
                        <div className="pt-2 border-t border-gray-100/50">
                          {renderMetadata(log)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* List Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            <span className="text-[11px] text-gray-500 font-bold uppercase tracking-widest">
              Live Feed • {filteredLogs.length} Events Logged
            </span>
          </div>
          
          {hasMore && !loading && (
            <button
              onClick={() => fetchLogs(page + 1, true)}
              disabled={loadingMore}
              className="px-6 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl text-xs font-bold hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 shadow-sm flex items-center justify-center transition-all min-w-[140px]"
            >
              {loadingMore ? (
                <><RefreshCw className="w-3.5 h-3.5 animate-spin mr-2 text-blue-600" /> Loading...</>
              ) : 'Fetch Older Records'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
